// calculateAvgMarketBuyPriceOptimized.ts
import Decimal from 'decimal.js';
import { Numeric } from '@/lib/types/common';
import { OrderBookItem } from '@/lib/types/order-book';

/**
 * Calculates the average price per share for a market buy order.
 *
 * Function assumes the `asks` array is already
 * pre-sorted by price from lowest to highest. Failing to provide a
 * sorted array will result in an incorrect calculation.
 *
 * @param amountUsd The total amount of USD to spend.
 * @param sortedAsks A pre-sorted array of ask orders (price ascending).
 * @returns The calculated average price per share as a Decimal. Returns
 *   Decimal(0) if the amount is zero, negative, or if no shares can be bought.
 */
export const calculateAvgMarketBuyPrice = (
  sortedAsks: OrderBookItem[],
  amountUsd: Numeric = Infinity,
): Decimal => {
  const amountToSpend = new Decimal(amountUsd);

  if (amountToSpend.isZero() || amountToSpend.isNegative() || sortedAsks.length === 0) {
    return new Decimal(0);
  }

  let remainingAmount = amountToSpend;
  let totalSharesBought = new Decimal(0);
  let totalCost = new Decimal(0);

  // No sort needed here - we iterate over the pre-sorted array directly.
  for (const ask of sortedAsks) {
    const levelCost = ask.price.times(ask.size);

    if (remainingAmount.greaterThanOrEqualTo(levelCost)) {
      totalCost = totalCost.plus(levelCost);
      totalSharesBought = totalSharesBought.plus(ask.size);
      remainingAmount = remainingAmount.minus(levelCost);
    } else {
      const sharesToBuy = remainingAmount.dividedBy(ask.price);
      totalCost = totalCost.plus(remainingAmount);
      totalSharesBought = totalSharesBought.plus(sharesToBuy);
      remainingAmount = new Decimal(0);
      break;
    }

    if (remainingAmount.isZero()) {
      break;
    }
  }

  if (totalSharesBought.isZero()) {
    return new Decimal(0);
  }

  // Return the average price of what was actually bought
  // Even if there wasn't enough liquidity, we show the real average price
  return totalCost.dividedBy(totalSharesBought);
};

/**
 * Calculates the number of shares that can be bought with a given amount.
 * This accounts for actual order book liquidity.
 *
 * @param sortedAsks A pre-sorted array of ask orders (price ascending).
 * @param amountUsd The total amount of USD to spend.
 * @returns The total number of shares that can be bought as a Decimal.
 *   Returns Decimal(0) if the amount is zero, negative, or if no shares can be bought.
 */
export const calculateMarketBuyShares = (
  sortedAsks: OrderBookItem[],
  amountUsd: Numeric = Infinity,
): Decimal => {
  const amountToSpend = new Decimal(amountUsd);

  if (amountToSpend.isZero() || amountToSpend.isNegative() || sortedAsks.length === 0) {
    return new Decimal(0);
  }

  let remainingAmount = amountToSpend;
  let totalSharesBought = new Decimal(0);

  for (const ask of sortedAsks) {
    const levelCost = ask.price.times(ask.size);

    if (remainingAmount.greaterThanOrEqualTo(levelCost)) {
      totalSharesBought = totalSharesBought.plus(ask.size);
      remainingAmount = remainingAmount.minus(levelCost);
    } else {
      const sharesToBuy = remainingAmount.dividedBy(ask.price);
      totalSharesBought = totalSharesBought.plus(sharesToBuy);
      remainingAmount = new Decimal(0);
      break;
    }

    if (remainingAmount.isZero()) {
      break;
    }
  }

  return totalSharesBought;
};

/**
 * Calculates the average price per share for a market sell order.
 *
 * Function assumes the `bids` array is already
 * pre-sorted by price from highest to lowest. Failing to provide a
 * sorted array will result in an incorrect calculation.
 *
 * @param sharesCount The total number of shares to sell.
 * @param sortedBids A pre-sorted array of bid orders (price descending).
 * @returns The calculated average price per share received from the sale.
 *   Returns Decimal(0) if the sharesCount is zero, negative, or if no
 *   shares can be sold.
 */
export const calculateAvgMarketSellPrice = (
  sortedBids: OrderBookItem[],
  sharesCount: Numeric = Infinity,
): Decimal => {
  const sharesToSell = Decimal(sharesCount);

  // Handle edge cases: no shares to sell or no bids available.
  if (sharesToSell.isZero() || sharesToSell.isNegative() || sortedBids.length === 0) {
    return Decimal(0);
  }

  let remainingShares = sharesToSell;
  let totalSharesSold = Decimal(0);
  let totalValueReceived = Decimal(0);

  // Iterate over the pre-sorted bids (highest price first).
  for (const bid of sortedBids) {
    if (remainingShares.greaterThanOrEqualTo(bid.size)) {
      // Our order is large enough to consume this entire bid level.
      totalValueReceived = totalValueReceived.plus(bid.price.times(bid.size));
      totalSharesSold = totalSharesSold.plus(bid.size);
      remainingShares = remainingShares.minus(bid.size);
    } else {
      // Our order is smaller than this bid level; it will be our last fill.
      totalValueReceived = totalValueReceived.plus(bid.price.times(remainingShares));
      totalSharesSold = totalSharesSold.plus(remainingShares);
      remainingShares = Decimal(0);
      break; // Exit the loop as the order is filled.
    }

    // If we've sold the exact amount, we can stop early.
    if (remainingShares.isZero()) {
      break;
    }
  }

  // If we couldn't sell any shares (e.g., no bids), return 0 to avoid division by zero.
  if (totalSharesSold.isZero()) {
    return Decimal(0);
  }

  // The average price is the total value received divided by total shares sold.
  return totalValueReceived.dividedBy(totalSharesSold);
};

/**
 * Gross proceeds of a market sell. The book may not absorb the whole size, and the
 * unfilled remainder brings nothing in — multiplying the sweep average by the
 * requested size (rather than the fillable one) overstates a sell into a thin book.
 */
export const calculateMarketSellProceeds = (
  sortedBids: OrderBookItem[],
  sharesCount: Numeric,
): Decimal => {
  const depth = sortedBids.reduce((acc, bid) => acc.plus(bid.size), new Decimal(0));
  const fillable = Decimal.min(new Decimal(sharesCount), depth);

  return calculateAvgMarketSellPrice(sortedBids, sharesCount).mul(fillable);
};
